import { Badge } from '@clay-run/glaze/badge';
import { Tooltip, TooltipPanel, TooltipTrigger } from '@clay-run/glaze/tooltip';
import clsx from 'clsx';
import type { AccountHierarchyNode } from '@shared/flow/account-hierarchy';
import { healthToneOf, type HealthTone } from '@shared/flow/account-hierarchy-whitespace';
import { sfdcRecordUrl } from '../../links';
import {
  HEALTH_BADGE_COLOR, LENS_COPY, reasonsLabel, renewalBadgeLabel, renewalTooltipFacts,
} from './hierarchyPresentation';

export type RenewalNode = Pick<
  AccountHierarchyNode,
  | 'id' | 'enterprise_renewal_date' | 'contracted_arr' | 'arr_active_subscriptions' | 'health_rating'
  | 'health_red_reason' | 'renewal_risk_reasons' | 'upcoming_renewal_opp_id'
>;

const HEALTH_TEXT: Record<HealthTone, string> = {
  success: 'text-content-success', warning: 'text-content-warning', danger: 'text-content-danger',
};

/** The renewal's facts, shared by the badge tooltip and the graph card's hover panel. */
export function RenewalFacts({ node }: { node: RenewalNode }) {
  const facts = renewalTooltipFacts(node);
  return (
    <dl className="flex flex-col gap-0.5 text-xs" data-testid={`renewal-facts-${node.id}`}>
      <div className="flex gap-1"><dt>Renewal date</dt><dd className="font-medium">{facts.date}</dd></div>
      <div className="flex gap-1"><dt>ARR at risk</dt><dd className="font-medium">{facts.arrAtRisk}</dd></div>
      {facts.health && (
        <div className="flex gap-1">
          <dt>Health</dt>
          <dd className={clsx('font-medium', facts.health.tone && HEALTH_TEXT[facts.health.tone])}>{facts.health.label}</dd>
        </div>
      )}
      {facts.reasons.length > 0 && (
        <div className="flex gap-1"><dt>Reasons</dt><dd className="font-medium">{reasonsLabel(facts.reasons)}</dd></div>
      )}
      {node.upcoming_renewal_opp_id && (
        <a
          href={sfdcRecordUrl('Opportunity', node.upcoming_renewal_opp_id)}
          target="_blank"
          rel="noreferrer"
          onClick={e => e.stopPropagation()}
          className="text-content-action hover:underline"
        >
          {LENS_COPY.openRenewalOpp}
        </a>
      )}
    </dl>
  );
}

export interface RenewalBadgeProps {
  node: RenewalNode;
  days: number;
  /** False where an outer tooltip (the graph card's) already shows `RenewalFacts`. */
  tooltip?: boolean;
}

/** "Renews in 45d", toned by the account's health (Green / Yellow / Red). */
export function RenewalBadge({ node, days, tooltip = true }: RenewalBadgeProps) {
  const tone = healthToneOf(node.health_rating);
  const badge = (
    <Badge size="xs" color={tone ? HEALTH_BADGE_COLOR[tone] : 'secondary'} data-testid={`renewal-badge-${node.id}`}>
      {renewalBadgeLabel(days)}
    </Badge>
  );
  if (!tooltip) return badge;
  return (
    <Tooltip>
      <TooltipTrigger>{badge}</TooltipTrigger>
      <TooltipPanel><RenewalFacts node={node} /></TooltipPanel>
    </Tooltip>
  );
}
